/**
 * Phase 4 Slice 3 — liege call as a quest: X turns to reach objective Y.
 * Pure module: march path + army token + deadline clock.
 * Battle resolve on arrival is Slice 4; UI chrome wires through call/main/render.
 */
import type { Season } from "./clock.ts";
import {
  scaleHostOriginToTotal,
  scaleStacksToTotal,
  type HostOriginMap,
  type TroopStacks,
} from "./troops.ts";
import type { HexMap } from "./map.ts";
import {
  beginMarch,
  armyHostOrigin,
  armyStacks,
  createArmyToken,
  syncArmyComposition,
  describeArmy,
  estimateTravelTurns,
  findPath,
  tickArmyMarch,
  type ArmyToken,
  type FoundPath,
} from "./march.ts";

/** Default deadline pad beyond estimated travel (turns). */
export const QUEST_DEADLINE_PAD = 2;

/** Minimum deadline even for short marches. */
export const QUEST_MIN_DEADLINE = 3;

export type QuestStatus =
  | "active"
  | "arrived"
  | "returning"
  | "won"
  | "failed"
  | "abandoned"
  | "stuck";

export type LiegeQuest = {
  id: string;
  /** Liege display name (for toasts). */
  liegeName: string;
  /** Origin (usually seat). */
  fromKey: string;
  /** Objective hex. */
  objectiveKey: string;
  /** Human label for objective (hall name / “field camp”). */
  objectiveLabel: string;
  /** Turn the quest was accepted. */
  startedTurn: number;
  /** Absolute turn by which the column must arrive (inclusive). */
  deadlineTurn: number;
  /** Turns left at last tick (deadlineTurn - currentTurn, floored at 0). */
  turnsLeft: number;
  /** Estimated travel when accepted (fixed-season snapshot). */
  estimatedTurns: number;
  pathCost: number;
  pathSteps: number;
  army: ArmyToken;
  status: QuestStatus;
  /** Last human-readable note. */
  note: string;
  /** Spears committed when the quest began (frozen for battle later). */
  strengthAtStart: number;
};

export type StartQuestInput = {
  id?: string;
  liegeName: string;
  map: HexMap;
  fromKey: string;
  objectiveKey: string;
  objectiveLabel?: string;
  season: Season;
  turn: number;
  strength: number;
  /** Discrete composition peeled for the column (5b3). */
  stacks?: TroopStacks | null;
  /** Ownership of stacks on the column (5b3). */
  hostOrigin?: HostOriginMap | null;
  armyId?: string;
  armyLabel?: string;
  /** Extra turns beyond estimate (default QUEST_DEADLINE_PAD). */
  deadlinePad?: number;
};

export type StartQuestResult =
  | { ok: true; quest: LiegeQuest; path: FoundPath }
  | { ok: false; reason: string };

export type TickQuestInput = {
  quest: LiegeQuest;
  map: HexMap;
  season: Season;
  /** Clock turn *after* advance, or the turn being resolved on End turn. */
  turn: number;
};

export type TickQuestResult = {
  quest: LiegeQuest;
  arrived: boolean;
  failed: boolean;
  stuck: boolean;
  log: string[];
};

/** Prefer the Phase 4.5 liege keep; else farthest bannerman; else farthest land cell. */
export function pickDefaultObjective(
  map: HexMap,
  fromKey: string,
): { key: string; label: string } | null {
  // Phase 4.5: path the call to the lord's keep when seeded
  if (map.liegeKey && map.liegeKey !== fromKey) {
    const p = findPath(map, fromKey, map.liegeKey, { season: "Summer" });
    if (p && p.steps >= 1) {
      const cell = map.cells.get(map.liegeKey);
      return {
        key: map.liegeKey,
        label: cell?.name ?? "Liege keep",
      };
    }
  }
  const banners = map.bannermanKeys.filter((k) => k !== fromKey);
  if (banners.length > 0) {
    // Prefer farthest banner so the march has teeth
    let best = banners[0]!;
    let bestD = -1;
    for (const k of banners) {
      const p = findPath(map, fromKey, k, { season: "Summer" });
      const d = p?.steps ?? 0;
      if (d > bestD) {
        bestD = d;
        best = k;
      }
    }
    const cell = map.cells.get(best);
    return { key: best, label: cell?.name ?? "Banner hall" };
  }
  // Fallback: any non-seat land cell with a summer path
  let fallback: { key: string; label: string; steps: number } | null = null;
  for (const [k, c] of map.cells) {
    if (k === fromKey) continue;
    if (c.biome === "water") continue;
    const p = findPath(map, fromKey, k, { season: "Summer" });
    if (!p || p.steps < 1) continue;
    if (!fallback || p.steps > fallback.steps) {
      fallback = {
        key: k,
        label: c.name ?? `Field ${k}`,
        steps: p.steps,
      };
    }
  }
  return fallback ? { key: fallback.key, label: fallback.label } : null;
}

export function startLiegeQuest(input: StartQuestInput): StartQuestResult {
  const strength = Math.max(0, Math.floor(input.strength));
  if (strength <= 0) {
    return { ok: false, reason: "No spears to march." };
  }
  const path = findPath(input.map, input.fromKey, input.objectiveKey, {
    season: input.season,
  });
  if (!path) {
    return {
      ok: false,
      reason: `No path to ${input.objectiveKey} under ${input.season} locks.`,
    };
  }
  const estimated = estimateTravelTurns(path.totalCost, input.season);
  const pad = input.deadlinePad ?? QUEST_DEADLINE_PAD;
  const window = Math.max(QUEST_MIN_DEADLINE, estimated + pad);
  const deadlineTurn = input.turn + window;
  const token = createArmyToken({
    id: input.armyId ?? `quest-host-${input.fromKey}`,
    label: input.armyLabel ?? `Host for ${input.liegeName}`,
    strength,
    atKey: input.fromKey,
    stacks: input.stacks,
    hostOrigin: input.hostOrigin,
  });
  const army = beginMarch(token, path, input.objectiveKey);
  if (army.status === "stuck") {
    return { ok: false, reason: army.note || "March could not begin." };
  }
  const alreadyThere = army.status === "arrived";
  const label = input.objectiveLabel ?? input.objectiveKey;
  const quest: LiegeQuest = {
    id: input.id ?? `quest-${input.turn}-${input.objectiveKey}`,
    liegeName: input.liegeName,
    fromKey: input.fromKey,
    objectiveKey: input.objectiveKey,
    objectiveLabel: label,
    startedTurn: input.turn,
    deadlineTurn,
    turnsLeft: alreadyThere ? 0 : window,
    estimatedTurns: estimated,
    pathCost: path.totalCost,
    pathSteps: path.steps,
    army,
    status: alreadyThere ? "arrived" : "active",
    note: alreadyThere
      ? `Column already at ${label}.`
      : `${input.liegeName} bids you reach ${label} within ${window} turn${window === 1 ? "" : "s"} (road ~${estimated}t).`,
    strengthAtStart: strength,
  };
  return { ok: true, quest, path };
}

/**
 * Advance the quest column one End turn.
 * Arrival before/on deadline → arrived (battle is Slice 4).
 * Past deadline without arrival → failed.
 * Season lock mid-path → stuck (still counts against deadline next ticks).
 */
export function tickLiegeQuest(input: TickQuestInput): TickQuestResult {
  const log: string[] = [];
  let q = input.quest;

  if (
    q.status === "won" ||
    q.status === "failed" ||
    q.status === "abandoned" ||
    q.status === "arrived"
  ) {
    return { quest: q, arrived: q.status === "arrived", failed: q.status === "failed", stuck: false, log };
  }

  const turnsLeft = Math.max(0, q.deadlineTurn - input.turn);
  let army = q.army;

  if (q.status === "active" || q.status === "stuck" || q.status === "returning") {
    if (army.status === "marching" || (q.status === "stuck" && army.path.length > 1)) {
      // Re-try stuck only if still marching path; tickArmyMarch handles impassable
      if (army.status === "stuck" && q.status !== "returning") {
        // Leave outbound stuck until abandoned; deadline may still fail them
        log.push(army.note || `${army.label} remains stuck.`);
      } else if (army.status === "stuck" && q.status === "returning") {
        log.push(army.note || `${army.label} remains stuck on the road home.`);
      } else {
        const step = tickArmyMarch(input.map, army, input.season);
        army = step.army;
        log.push(...step.log);
      }
    } else if (army.status === "arrived") {
      // fall through
    } else if (army.status === "idle") {
      log.push(`${army.label} idle — quest stalled.`);
    }
  }

  // Home leg: arrival is seat (fromKey), not the field objective.
  if (q.status === "returning") {
    const home = q.fromKey;
    if (army.status === "arrived" || army.atKey === home) {
      const note = `${army.label} returns to the seat with ${Math.max(0, Math.floor(army.strength))} spears.`;
      log.push(note);
      q = {
        ...q,
        army: { ...army, status: "arrived", atKey: home },
        status: "arrived",
        turnsLeft: 0,
        note,
      };
      return { quest: q, arrived: true, failed: false, stuck: false, log };
    }
    if (army.status === "stuck") {
      const note = army.note || `${army.label} stuck on the road home.`;
      log.push(note);
      q = { ...q, army, status: "returning", turnsLeft: Math.max(1, turnsLeft), note };
      return { quest: q, arrived: false, failed: false, stuck: true, log };
    }
    const note =
      log[log.length - 1] ??
      `${army.label} marching home · ${describeArmy(army)}`;
    q = {
      ...q,
      army,
      status: "returning",
      turnsLeft: Math.max(1, turnsLeft),
      note,
    };
    return { quest: q, arrived: false, failed: false, stuck: false, log };
  }

  // Outbound objective only (returning already returned above).
  if (army.status === "arrived" || army.atKey === q.objectiveKey) {
    const onTime = input.turn <= q.deadlineTurn;
    if (onTime) {
      const note = `${army.label} reaches ${q.objectiveLabel} with ${turnsLeft} turn${turnsLeft === 1 ? "" : "s"} to spare.`;
      log.push(note);
      q = {
        ...q,
        army: { ...army, status: "arrived" },
        status: "arrived",
        turnsLeft,
        note,
      };
      return { quest: q, arrived: true, failed: false, stuck: false, log };
    }
    // Arrived late
    const note = `${army.label} reaches ${q.objectiveLabel} late — ${q.liegeName} marks the quest failed.`;
    log.push(note);
    q = {
      ...q,
      army: { ...army, status: "arrived" },
      status: "failed",
      turnsLeft: 0,
      note,
    };
    return { quest: q, arrived: true, failed: true, stuck: false, log };
  }

  if (army.status === "stuck") {
    const note = army.note || `${army.label} stuck on the road to ${q.objectiveLabel}.`;
    if (input.turn > q.deadlineTurn) {
      const fail = `${q.liegeName}'s quest failed — column stuck and deadline passed.`;
      log.push(fail);
      q = { ...q, army, status: "failed", turnsLeft: 0, note: fail };
      return { quest: q, arrived: false, failed: true, stuck: true, log };
    }
    q = { ...q, army, status: "stuck", turnsLeft, note };
    return { quest: q, arrived: false, failed: false, stuck: true, log };
  }

  // Still marching outbound — check deadline
  if (input.turn > q.deadlineTurn) {
    const note = `${q.liegeName}'s quest failed — deadline passed before ${q.objectiveLabel} (column at ${army.atKey}).`;
    log.push(note);
    q = {
      ...q,
      army,
      status: "failed",
      turnsLeft: 0,
      note,
    };
    return { quest: q, arrived: false, failed: true, stuck: false, log };
  }

  const note =
    log[log.length - 1] ??
    `${army.label} en route to ${q.objectiveLabel} · ${turnsLeft}t left · ${describeArmy(army)}`;
  q = {
    ...q,
    army,
    status: "active",
    turnsLeft,
    note,
  };
  return { quest: q, arrived: false, failed: false, stuck: false, log };
}

/** Player walks away from an open quest (favor hit applied by caller). */
export function abandonLiegeQuest(quest: LiegeQuest, reason?: string): LiegeQuest {
  return {
    ...quest,
    status: "abandoned",
    turnsLeft: 0,
    note: reason ?? `Abandoned march on ${quest.objectiveLabel}.`,
    army: {
      ...quest.army,
      status: quest.army.status === "arrived" ? "arrived" : "idle",
      note: reason ?? "Column stood down.",
    },
  };
}

/** Mark quest won after battle (Slice 4 hooks here). */
/**
 * After field resolve: march survivors home to fromKey.
 * Keeps strength on the army token until seat arrival (call folds into host, not garrison).
 */

/**
 * Apply survivor headcount to a quest column, scaling stacks + hostOrigin (5b3).
 */
export function applyQuestSurvivorCount(quest: LiegeQuest, survivors: number): LiegeQuest {
  const target = Math.max(0, Math.floor(survivors));
  const stacks = scaleStacksToTotal(armyStacks(quest.army), target);
  const origin = scaleHostOriginToTotal(armyHostOrigin(quest.army), target);
  const army = syncArmyComposition(quest.army, { stacks, hostOrigin: origin });
  return {
    ...quest,
    army,
    strengthAtStart: quest.strengthAtStart, // historical; leave
  };
}

export function beginQuestReturn(
  quest: LiegeQuest,
  map: HexMap,
  season: Season,
  turn: number,
): { ok: true; quest: LiegeQuest; path: FoundPath } | { ok: false; reason: string } {
  void turn;
  // 5b3: strength follows discrete stacks when present
  const baseArmy = syncArmyComposition(quest.army);
  const strength = Math.max(0, Math.floor(baseArmy.strength));
  if (strength <= 0) {
    return { ok: false, reason: "No survivors to march home." };
  }
  const fromKey = baseArmy.atKey || quest.objectiveKey;
  const homeKey = quest.fromKey;
  if (fromKey === homeKey) {
    const army = {
      ...baseArmy,
      strength,
      atKey: homeKey,
      status: "arrived" as const,
      path: [homeKey],
      pathIndex: 0,
      destKey: homeKey,
      mpResidual: 0,
      note: "Column home at seat.",
    };
    return {
      ok: true,
      path: { keys: [homeKey], totalCost: 0, steps: 0, usedRoadEdges: 0 },
      quest: {
        ...quest,
        army,
        status: "arrived",
        turnsLeft: 0,
        note: `Survivors home at seat (${strength}).`,
      },
    };
  }
  const path = findPath(map, fromKey, homeKey, { season });
  if (!path) {
    return {
      ok: false,
      reason: `No path home to ${homeKey} under ${season} locks.`,
    };
  }
  const estimated = estimateTravelTurns(path.totalCost, season);
  const token = {
    ...baseArmy,
    strength,
    atKey: fromKey,
    status: "idle" as const,
    path: [] as string[],
    pathIndex: 0,
    destKey: homeKey,
    mpResidual: 0,
    turnsMarching: 0,
    note: "Returning to seat.",
  };
  const army = beginMarch(token, path, homeKey);
  if (army.status === "stuck") {
    return { ok: false, reason: army.note || "Return march could not begin." };
  }
  const already = army.status === "arrived";
  return {
    ok: true,
    path,
    quest: {
      ...quest,
      army,
      status: already ? "arrived" : "returning",
      // Return leg is not under the original liege deadline — clear pressure clock.
      turnsLeft: already ? 0 : Math.max(1, estimated + QUEST_DEADLINE_PAD),
      estimatedTurns: estimated,
      pathCost: path.totalCost,
      pathSteps: path.steps,
      note: already
        ? `Survivors already at seat (${strength}).`
        : `Returning ${strength} to seat (~${estimated}t).`,
      // Keep deadlineTurn as historical; return does not fail on it.
    },
  };
}

export function completeLiegeQuest(quest: LiegeQuest, note?: string): LiegeQuest {
  return {
    ...quest,
    status: "won",
    turnsLeft: 0,
    note: note ?? `Victory at ${quest.objectiveLabel}.`,
  };
}

export function describeQuest(q: LiegeQuest): string {
  const head = `${q.liegeName}: ${q.objectiveLabel} (${q.objectiveKey})`;
  if (q.status === "returning") {
    const n = Math.max(0, Math.floor(q.army.strength));
    return `${head} · marching home · ${n} spears · at ${q.army.atKey} → ${q.fromKey}`;
  }
  if (q.status === "active" || q.status === "stuck") {
    return `${head} · ${q.turnsLeft}t left · ${describeArmy(q.army)}`;
  }
  if (q.status === "failed") {
    return `${head} · failed — ${q.note || "column stood down"}`;
  }
  if (q.status === "won" || q.status === "arrived") {
    return `${head} · ${q.status} — ${q.note || "done"}`;
  }
  return `${head} · ${q.status} — ${q.note}`;
}

export function isQuestOpen(q: LiegeQuest | null | undefined): boolean {
  return !!q && (q.status === "active" || q.status === "stuck" || q.status === "returning");
}
